import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import CustomSearch from '../CustomSearch/CustomSearch';
import useCorsi from '../../hooks/useCorsi';
import CustomModal from '../CustomModal/CustomModal';
import useVoucherSuperpartner from '../../hooks/useVoucherSuperpartner';

export default function GestioneVoucherSuperpartner({ show, onHide, superpartner, onUpdate }) {
    const { getCorsiByParams } = useCorsi();
    const { isLoading, updateVoucherQuantity, getHistory, getVoucherStock } = useVoucherSuperpartner();

    const [selectedCourse, setSelectedCourse] = useState(null);
    const [quantita, setQuantita] = useState('');
    const [operazione, setOperazione] = useState('aumento');
    const [note, setNote] = useState('');
    const [history, setHistory] = useState([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [currentStock, setCurrentStock] = useState(null);

    useEffect(() => {
        const fetchDetails = async () => {
            if (superpartner && selectedCourse) {
                setIsHistoryLoading(true);
                const [historyData, stockData] = await Promise.all([
                    getHistory({ id_superpartner: superpartner.id_superpartner, id_corso: selectedCourse.id_corso }),
                    getVoucherStock({ id_superpartner: superpartner.id_superpartner, id_corso: selectedCourse.id_corso })
                ]);
                setHistory(historyData || []);
                setCurrentStock(stockData);
                setIsHistoryLoading(false);
            } else {
                setHistory([]);
                setCurrentStock(null);
            }
        };
        fetchDetails();
    }, [selectedCourse, superpartner, getHistory, getVoucherStock]);

    const handleSave = async () => {
        const data = {
            id_superpartner: superpartner.id_superpartner,
            id_corso: selectedCourse.id_corso,
            quantita: parseInt(quantita, 10),
            operazione: operazione,
            note: note
        };
        const success = await updateVoucherQuantity(data);
        if (success) {
            onUpdate(); 
            onHide();
        }
    };

    const renderHistoryTable = () => (
        <div className="mt-4">
            <hr />
            <h6>Storico Operazioni</h6>
            {isHistoryLoading ? <div className="text-center p-3"><Spinner size="sm" /></div> :
            history.length > 0 ? (
                <Table striped bordered hover size="sm" style={{ tableLayout: 'fixed', width: '100%' }}>
                    <thead className="table-light">
                        <tr>
                            <th style={{ width: '150px' }}>Data</th>
                            <th style={{ width: '120px' }}>Operazione</th>
                            <th style={{ width: '100px' }}>Quantità</th>
                            <th>Note</th>
                        </tr>
                    </thead>
                    <tbody>
                        {history.map(item => (
                            <tr key={item.id_voucher_superpartner_assegnazione}>
                                <td>{new Date(item.data_operazione).toLocaleString('it-IT')}</td>
                                <td>
                                    {item.aumento > 0 && <Badge bg="success">Aumento</Badge>}
                                    {item.decremento > 0 && <Badge bg="danger">Decremento</Badge>}
                                </td>
                                <td>{item.variazione}</td>
                                <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item.note || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            ) : (
                <p className="text-muted small">Nessuna operazione registrata per questo corso.</p>
            )}
        </div>
    );

    const renderBody = () => (
        <div className='container-fluid px-1'>
            <Alert variant='light'>
                Stai modificando il magazzino voucher per: <strong>{superpartner.nome}</strong>
            </Alert>

            {/* --- BLOCCO CORRETTO PER LA SELEZIONE DEL CORSO --- */}
            {!selectedCourse ? (
                <CustomSearch 
                    field={"denominazione"} 
                    label={"1. Seleziona un corso"} 
                    searchApi={getCorsiByParams} 
                    placeholder={"Cerca per nome del corso..."} 
                    selectedItem={selectedCourse} 
                    setSelectedItem={setSelectedCourse}
                />
            ) : (
                <div className="p-2 bg-light border rounded">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <span className="small text-muted d-block"><b>Corso Selezionato</b></span>
                            <span>{selectedCourse.denominazione}</span>
                        </div>
                        <Button variant="outline-secondary" size="sm" onClick={() => setSelectedCourse(null)}>Cambia</Button>
                    </div>
                </div>
            )}
            
            {selectedCourse && (
                <>
                    <Alert variant="secondary" className="mt-3">
                        Scorta attuale per questo corso: <strong>{currentStock ? currentStock.qt_disponibile : '...'}</strong> voucher.
                    </Alert>

                    <h6 className="mt-4">2. Esegui Operazione</h6>
                    <Form.Group as={Row} className="my-3 align-items-center">
                        <Form.Label column sm={3}>Operazione</Form.Label>
                        <Col sm={9}>
                            <Form.Check inline type="radio" label="Aumento" name="operazione" value="aumento" checked={operazione === 'aumento'} onChange={(e) => setOperazione(e.target.value)} />
                            <Form.Check inline type="radio" label="Decremento" name="operazione" value="decremento" checked={operazione === 'decremento'} onChange={(e) => setOperazione(e.target.value)} />
                        </Col>
                    </Form.Group>
                    <Form.Group className="my-3">
                        <Form.Label>Quantità Voucher</Form.Label>
                        <Form.Control type="number" value={quantita} onChange={(e) => setQuantita(e.target.value)} placeholder="Es. 50" min="1" />
                    </Form.Group>
                    <Form.Group className="my-3">
                        <Form.Label>Note (opzionale)</Form.Label>
                        <Form.Control as="textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Es. Carico iniziale per campagna estiva..." />
                    </Form.Group>
                    
                    {renderHistoryTable()}
                </>
            )}
        </div>
    );

    return (
        <CustomModal
            show={show}
            onHide={() => { onHide(); setSelectedCourse(null); }} // Pulisce il corso alla chiusura
            headerText="Gestione Magazzino Voucher Superpartner"
            body={renderBody()}
            onSave={handleSave}
            isLoading={isLoading}
            btnText={"Conferma Operazione"}
            onUndoChanges={() => { onHide(); setSelectedCourse(null); }} // Pulisce il corso
            disabled={!selectedCourse || !quantita || parseInt(quantita) <= 0}
        />
    );
}